/**
 * Document API — Connect RPC implementation
 * Calls DocumentService (baltigo) via sabaody gateway for standalone document operations.
 * Returns the same types as the REST documentApi.
 */ import { documentClient } from '../connect/clients';
function timestampToString(ts) {
    if (!ts) return '';
    return new Date(Number(ts.seconds) * 1000).toISOString();
}
function toDocumentStatus(s) {
    switch(s){
        case 'queued':
        case 'processing':
        case 'error':
        case 'none':
            return s;
        case 'running':
            return 'processing';
        case 'completed':
            return 'ready';
        case 'failed':
            return 'error';
        case 'pending':
            return 'queued';
        default:
            return 'none';
    }
}
export const documentApiConnect = {
    async listDocuments (params = {}) {
        const response = await documentClient.listDocuments({
            limit: params.limit ?? 50,
            offset: params.offset ?? 0,
            status: params.status ?? '',
            sortBy: params.sortBy ?? 'recent',
            projectId: params.projectId ?? ''
        });
        const documents = (response.documents ?? []).map((d)=>({
                id: d.id,
                filename: d.filename,
                contentType: d.contentType,
                size: Number(d.size),
                status: toDocumentStatus(d.status),
                createdAt: timestampToString(d.createdAt),
                updatedAt: timestampToString(d.updatedAt),
                error: d.errorMessage || undefined,
                projectId: d.projectId || undefined
            }));
        const pagination = {
            total: response.pagination?.total ?? 0,
            limit: response.pagination?.limit ?? 50,
            offset: response.pagination?.offset ?? 0,
            hasMore: response.pagination?.hasMore ?? false
        };
        return {
            documents,
            pagination
        };
    },
    async processDocument (file, options = {}) {
        const buffer = await file.arrayBuffer();
        const response = await documentClient.processDocument({
            fileData: new Uint8Array(buffer),
            filename: file.name,
            contentType: file.type || 'application/octet-stream',
            outputFormat: options.outputFormat ?? 'markdown',
            options: {
                useDeepProcessing: options.useDeepProcessing ?? false,
                extractTables: options.extractTables ?? true,
                extractImages: options.extractImages ?? false,
                preserveLayout: options.preserveLayout ?? true,
                chunkSize: options.chunkSize ?? 0,
                overlapSize: options.overlapSize ?? 0
            }
        });
        return {
            id: response.id,
            status: response.status,
            filename: file.name,
            jobId: response.id,
            message: 'Document submitted for processing'
        };
    },
    async getJobStatus (jobId) {
        const response = await documentClient.getDocumentStatus({
            id: jobId
        });
        // Map baltigo status to the job status format the frontend expects
        const statusMap = {
            processing: 'running',
            queued: 'pending',
            completed: 'completed',
            failed: 'failed'
        };
        const rawStatus = response.status;
        const status = statusMap[rawStatus] ?? rawStatus;
        return {
            job_id: response.id || jobId,
            status,
            error: response.errorMessage || undefined
        };
    },
    async deleteDocuments (ids) {
        const response = await documentClient.deleteDocuments({
            ids
        });
        return {
            deleted: response.deleted
        };
    },
    async downloadDocument (jobId) {
        const response = await documentClient.downloadDocument({
            id: jobId
        });
        const contentType = response.contentType || 'text/markdown';
        const blob = new Blob([
            response.data
        ], {
            type: contentType
        });
        return {
            blob,
            filename: response.filename || `document-${jobId}`
        };
    }
};
